import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@auth0/auth0-spa-js';
import { getAuth0Client } from './auth0Client';
import { loginViaOfficeDialog, type AuthConnection } from './officeDialogAuth';
import { isOfficeTaskPane } from './environment';
import {
  clearStoredTokens,
  decodeIdToken,
  getStoredTokens,
  isTokenValid,
  type StoredTokens,
} from './tokenStore';
import { refreshAccessToken } from './officeDialogAuth';

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated'; error?: string }
  | { status: 'authenticated'; user: User };

interface AuthContextValue {
  state: AuthState;
  /**
   * Start the login flow. Pass an explicit Auth0 connection to skip the
   * chooser — `'google-oauth2'` for Google SSO or
   * `'Username-Password-Authentication'` for email + password.
   */
  login: (connection?: AuthConnection) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      // Prefer our token store (used by Office Dialog flow). Fall back to
      // auth0-spa-js for the browser popup path.
      const stored = getStoredTokens();
      if (stored) {
        if (!isTokenValid(stored) && stored.refreshToken) {
          try {
            const refreshed = await refreshAccessToken(stored.refreshToken);
            const claims = decodeIdToken(refreshed.idToken) ?? {};
            setState({ status: 'authenticated', user: claimsToUser(claims) });
            return;
          } catch {
            clearStoredTokens();
          }
        } else if (isTokenValid(stored)) {
          const claims = decodeIdToken(stored.idToken) ?? {};
          setState({ status: 'authenticated', user: claimsToUser(claims) });
          return;
        }
      }

      // auth0-spa-js path (browser)
      const client = await getAuth0Client();
      const authed = await client.isAuthenticated();
      if (!authed) {
        setState({ status: 'unauthenticated' });
        return;
      }
      const user = await client.getUser();
      setState(
        user ? { status: 'authenticated', user } : { status: 'unauthenticated' },
      );
    } catch (err) {
      setState({
        status: 'unauthenticated',
        error: err instanceof Error ? err.message : 'Failed to initialize auth',
      });
    }
  }, []);

  const login = useCallback(
    async (connection?: AuthConnection) => {
      try {
        setState({ status: 'loading' });
        if (isOfficeTaskPane()) {
          const tokens = await loginViaOfficeDialog({ connection });
          const claims = decodeIdToken(tokens.idToken) ?? {};
          setState({ status: 'authenticated', user: claimsToUser(claims) });
          return;
        }
        // Browser: auth0-spa-js popup (works in real browsers, blocked in Word WebView)
        const client = await getAuth0Client();
        await client.loginWithPopup({
          authorizationParams: {
            // screen_hint=login keeps the popup strictly a login surface —
            // signup lives at app.nyayassist.ai (phone verification + profile
            // onboarding), not here. Users without accounts hit AuthGate's
            // "Account not found" state and get redirected out.
            screen_hint: 'login',
            ...(connection ? { connection } : {}),
            // Account picker only makes sense for Google; for the DB connection
            // Auth0 handles re-auth against the cookie state itself.
            ...(connection === 'google-oauth2' ? { prompt: 'select_account' } : {}),
          },
        });
        await refresh();
      } catch (err) {
        setState({
          status: 'unauthenticated',
          error: err instanceof Error ? err.message : 'Sign in failed',
        });
      }
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    clearStoredTokens();
    try {
      const client = await getAuth0Client();
      await client.logout({
        logoutParams: { returnTo: window.location.origin },
        openUrl: async () => {
          /* no-op: avoid navigating the whole task pane on logout */
        },
      });
    } catch {
      // clearStoredTokens already did the important work
    }
    setState({ status: 'unauthenticated' });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ state, login, logout, refresh }),
    [state, login, logout, refresh],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

function claimsToUser(claims: Record<string, unknown>): User {
  return {
    sub: typeof claims.sub === 'string' ? claims.sub : undefined,
    email: typeof claims.email === 'string' ? claims.email : undefined,
    name: typeof claims.name === 'string' ? claims.name : undefined,
    picture: typeof claims.picture === 'string' ? claims.picture : undefined,
    email_verified:
      typeof claims.email_verified === 'boolean' ? claims.email_verified : undefined,
  } as User;
}

// Re-export for convenience
export type { StoredTokens };
